import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { noop } from "lodash-es";

type JustPinnedContextValue = {
  isJustPinned: (id: string) => boolean;
  markPinned: (id: string) => void;
  dismiss: (id: string) => void;
};

const JustPinnedContext = createContext<JustPinnedContextValue>({
  isJustPinned: () => false,
  markPinned: noop,
  dismiss: noop,
});

// Holds the "just pinned" callout state for one dashboard view. Scope it by
// remounting with a `key` on the dashboard identity, and leaving the page
// unmounts it — so the callouts reset without any effect/cleanup (which keeps
// it safe under Strict Mode's double-invoked mount).
export const JustPinnedProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());

  const markPinned = useCallback(
    (id: string) => setIds((prev) => new Set(prev).add(id)),
    [],
  );
  const dismiss = useCallback(
    (id: string) =>
      setIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      }),
    [],
  );

  const value = useMemo<JustPinnedContextValue>(
    () => ({ isJustPinned: (id) => ids.has(id), markPinned, dismiss }),
    [ids, markPinned, dismiss],
  );

  return <JustPinnedContext value={value}>{children}</JustPinnedContext>;
};

export const useJustPinned = (): JustPinnedContextValue =>
  useContext(JustPinnedContext);
